import { AloftPrice, isDefined, isUndefined } from '@aloft/toolkit-shared';

import { Typography } from '../../../summary/components/Typography.tsx';
import { SummarySection } from '../SummarySection.tsx';
import { SummaryTableHorizontal } from '../SummaryTableHorizontal.tsx';
import { TitleAndExportToggleLockup } from '../TitleAndExportToggleLockup.tsx';
import { BreakdownColumn, Tkv2PairedCostSummaryInterfaceV2 } from '../types.ts';
import { adjustment } from '../utils/formatters/adjustment.ts';
import { wholeDollar } from '../utils/formatters/wholeDollar.ts';
import { getTableData } from '../utils/getTableData.ts';

const tableDef: BreakdownColumn[] = [
  {
    header: 'Quality',
    columns: [
      { type: 'feature', adjustmentType: 'quality', path: 'uadQuality' },
      { type: 'feature', adjustmentType: 'quality', path: 'dwellingCostQuality' },
    ],
    combiner: ([uadQuality, dwellingCostQuality]) => {
      return `${isDefined(uadQuality) ? uadQuality : '-'} | ${
        isDefined(dwellingCostQuality) ? dwellingCostQuality : '-'
      }`;
    },
  },
  {
    header: 'Difference in $/SF',
    type: 'feature',
    adjustmentType: 'quality',
    path: 'costPerSqftDiff',
    valueFormatter: wholeDollar,
  },
  {
    header: 'x Dwelling Cost New',
    type: 'feature',
    adjustmentType: 'quality',
    path: 'costNew',
    valueFormatter: wholeDollar,
  },
  {
    header: '= Adjustment',
    type: 'adjustment',
    adjustmentType: 'quality',
    path: 'adjustment',
    valueFormatter: adjustment,
  },
];

export const AdjustmentBreakdownDepreciatedCostMainDwellingQuality = (
  props: Tkv2PairedCostSummaryInterfaceV2,
) => {
  const tableData = getTableData(tableDef, props, props.compSequence);

  const sortedPrices = Object.values(tableData)
    .map((row) => row[1])
    .filter(AloftPrice.isAloftPrice)
    .sort((a, b) => (a as AloftPrice).compare(b as AloftPrice));

  const minString = `${sortedPrices[0]?.toString(true)}/sf`;
  const maxString = `${sortedPrices[sortedPrices.length - 1]?.toString(true)}/sf`;
  const rangeString = [minString, maxString].join(' to ');

  if (isUndefined(minString) && isUndefined(maxString)) {
    return null;
  }

  return (
    <SummarySection marginBottom="2rem">
      <TitleAndExportToggleLockup
        {...props}
        title="Quality"
        id="isDepreciatedCostMainDwellingQualityInExport"
        variant="h3"
      />
      <Typography variant="body1">
        {`The quality adjustment is the difference in depreciated cost per square foot between the subject and the comparable, after adjusting for condition and bath fixtures. These differences ranged from ${rangeString}. Note: design and location can also impact cost per square foot.`}
      </Typography>
      <SummaryTableHorizontal
        {...props}
        summary={props}
        compSequence={props.compSequence}
        columns={tableDef}
        tableData={tableData}
        subjectHeader="SUB"
        compHeaderPrefix="CP"
      />
    </SummarySection>
  );
};
